import React, { JSX } from "react";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { Box, Button, Tooltip } from "@mui/material";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import moment from "moment-timezone";
import Panel from "../../components/Panel";
import AssessmentContent from "../Modals/Assessment/AssessmentContent";
import { Assessment, AssessmentDocument } from "../../interfaces/AssessmentInterfaces";
import OkhatiDialog from "../../components/Dialog/Dialog";
import { tl } from "../../components/translate";
import MultipleButtonGroup, { PRINT_FROM } from "../../components/MultipleButtonGroup";
import Print from "./Print/Print";
import { useAppSelector } from "../../store/hooks";
import { getClientAllergies } from "../../api/allergies";
import { Allergy } from "../../interfaces/Allergy";
import { UserMode } from "../../interfaces/User";
import * as NotificationActions from "../../actions/notification";
import { errorFetchMessage, MODULE } from "../../helpers/messages";
import hasOwnProperty from "../../helpers/object";
import ComparePanel from "./ComparePanel";
import CopyToClipboard from "../../components/ClipboardCopy/ClipboardCopy";
import { getAppBaseUrl } from "../../helpers/urls";
import { AssessmentTabularPrint } from "./Print/TabularTemplate/AssessmentTabularPrint";
import useGetShowAdAndBsAtOnce from "../../hooks/useGetShowAdAndBsAtOnce";

export const isRiskInfoOnPrintVisibleItemsFromStorage = (): string | null =>
  localStorage.getItem("isRiskInfoOnPrintVisible");

const showCreatedDateOpdItemsFromLocalStorage = () =>
  JSON.parse(localStorage.getItem("showCreationDateInADOnOpdPrint") || "{}");

interface Props {
  onClose: () => void;
  assessment: AssessmentDocument;
  setAssessmentCopy?: React.Dispatch<React.SetStateAction<Assessment>>;
  disableCarryForward?: boolean;
  wrapperStyle?: React.CSSProperties;
}
export default function AssessmentInfoPanel({
  onClose,
  assessment,
  setAssessmentCopy,
  disableCarryForward = false,
  wrapperStyle = { maxWidth: "720px", minWidth: "200px", width: "100%" }
}: Props): JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const dispatch = useDispatch();
  const [riskInfoFlag, setRiskInfoFlag] = React.useState<Record<string, boolean>>({});
  const isRiskInfoVisibleOnCurrentAssessment = riskInfoFlag[assessment.id];
  const authenticable = useAppSelector((state) => state.userContext.userCreds.authenticable);
  const isAssessmentEditable = moment().diff(moment(assessment.created_at), "day", true) < 3;
  const [clientAllergies, setClientAllergies] = React.useState<Allergy[]>([]);
  const [showCreationDateInADOnPrint, setShowCreationDateInADOnPrint] = React.useState(false);
  const [showComparePanel, setShowComparePanel] = React.useState(false);
  const isClientPortal = useAppSelector((state) => state.isClientPortal);
  const { showAdAndBsAtOnce, toggleShowAdAndBsAtOnce } = useGetShowAdAndBsAtOnce();

  React.useEffect(() => {
    const riskItems = isRiskInfoOnPrintVisibleItemsFromStorage();
    const showInADItems = showCreatedDateOpdItemsFromLocalStorage();
    if (riskItems) {
      setRiskInfoFlag(JSON.parse(riskItems));
    }
    if (showInADItems && hasOwnProperty(showInADItems, assessment.id)) {
      setShowCreationDateInADOnPrint(showInADItems[assessment.id]);
    }

    (async () => {
      try {
        const response = await getClientAllergies(assessment.clientId);
        setClientAllergies(response);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        dispatch(
          NotificationActions.notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: error?.data?.message || errorFetchMessage(MODULE.RISK_INFO),
            autoTimeout: true
          })
        );
      }
    })();
  }, [assessment.clientId, assessment.id, dispatch]);

  const onShowADInPrintToggle = (id: number, checked: boolean) => {
    localStorage.setItem(
      "showCreationDateInADOnOpdPrint",
      JSON.stringify({ ...showCreatedDateOpdItemsFromLocalStorage(), [id]: checked })
    );
    setShowCreationDateInADOnPrint(checked);
  };

  const onShowRiskInfoToggle = (id: number) => {
    const newValue = {
      ...riskInfoFlag,
      [id]: !riskInfoFlag[id]
    };
    setRiskInfoFlag(newValue);
    localStorage.setItem("isRiskInfoOnPrintVisible", JSON.stringify(newValue));
  };

  const ActionButtons = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {authenticable !== UserMode.Client && (
        <Tooltip
          arrow
          title={
            isAssessmentEditable ? "" : "Assessment can only be edited within 24 hours of creation."
          }
        >
          <Button
            variant="outlined"
            size="small"
            data-testmation="assessmentEditButton"
            disabled={!isAssessmentEditable}
            onClick={() => {
              dispatch(
                push(`/assessment/new/${assessment.id}/edit/?clientId=${assessment.clientId}`)
              );
            }}
          >
            {tl("edit")}
          </Button>
        </Tooltip>
      )}
      {!disableCarryForward && (
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            setIsDialogOpen(true);
          }}
        >
          {tl("assessment.carryForward")}
        </Button>
      )}
      {!!assessment.attachments.length && (
        <Tooltip title="Compare assessment images">
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setShowComparePanel(true);
            }}
          >
            {tl("assessment.compare")}
          </Button>
        </Tooltip>
      )}
    </Box>
  );

  const PrintButton = (
    <MultipleButtonGroup
      assessmentId={assessment.id}
      isRiskInfoOnPrintVisible={isRiskInfoVisibleOnCurrentAssessment}
      onShowRiskInfoToggle={onShowRiskInfoToggle}
      setShowDateInAD={onShowADInPrintToggle}
      showDateInAD={showCreationDateInADOnPrint}
      showAdAndBsAtOnce={showAdAndBsAtOnce}
      setShowAdAndBsAtOnce={() => toggleShowAdAndBsAtOnce()}
      printFrom={PRINT_FROM.ASSESSMENT}
      options={[
        {
          label: "Print Default",
          onClickAction: () => ({}),
          component: (
            <Print
              clientAllergies={clientAllergies}
              buttonText="Print Default"
              key="assessmentPrint"
              assessment={assessment}
              isRiskInfoVisible={isRiskInfoVisibleOnCurrentAssessment}
              isCreationDateInADVisible={showCreationDateInADOnPrint}
              showAdAndBsAtOnce={showAdAndBsAtOnce}
            />
          )
        },
        ...(!isClientPortal
          ? [
              {
                label: "Print Doctor",
                onClickAction: () => ({}),
                component: (
                  <Print
                    clientAllergies={clientAllergies}
                    buttonText="Print Doctor"
                    key="assessmentPrint"
                    assessment={assessment}
                    isRiskInfoVisible={isRiskInfoVisibleOnCurrentAssessment}
                    isCreationDateInADVisible={showCreationDateInADOnPrint}
                    showAdAndBsAtOnce={showAdAndBsAtOnce}
                  />
                )
              }
            ]
          : []),
        {
          label: "Tabular Print",
          onClickAction: () => ({}),
          component: (
            <AssessmentTabularPrint
              clientAllergies={clientAllergies}
              buttonText="Tabular Print"
              key="assessmentPrint"
              assessment={assessment}
              isRiskInfoVisible={isRiskInfoVisibleOnCurrentAssessment}
              isCreationDateInADVisible={showCreationDateInADOnPrint}
              showAdAndBsAtOnce={showAdAndBsAtOnce}
            />
          )
        }
      ]}
    />
  );

  return (
    <div>
      <Panel
        onClose={onClose}
        title="Details"
        genericButton={
          assessment.assessmentUUID && (
            <CopyToClipboard
              TooltipProps={{
                title: "Bill link is copied to clipboard!"
              }}
            >
              {({ copy }) => (
                <Tooltip title="Copy link" aria-label="copylink">
                  <FileCopyIcon
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      copy(`${getAppBaseUrl()}/my-report/${assessment.assessmentUUID}`);
                    }}
                  />
                </Tooltip>
              )}
            </CopyToClipboard>
          )
        }
        // printButton={PrintButton}
        wrapperStyle={wrapperStyle}
        contentStyle={{ py: 0, px: 0 }}
      >
        <Box
          sx={{
            px: 4,
            py: 1.25,
            borderBottom: 1,
            borderColor: "divider"
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            {ActionButtons}
            {PrintButton}
          </Box>
        </Box>

        <Box sx={{ px: 4, py: 2 }}>
          <AssessmentContent data={assessment} assessmentDoc={assessment.assessmentDocument} />
        </Box>

        {isDialogOpen && (
          <OkhatiDialog
            title={tl("assessment.carryForwardTitle")}
            description={tl("assessment.confirmCarryForward")}
            next={async () => {
              if (setAssessmentCopy) {
                setAssessmentCopy({ ...assessment, createdDate: new Date() });
              }
              dispatch(
                push(`/assessment/new/copy?clientId=${assessment.clientId}&id=${assessment.id}`)
              );
              setIsDialogOpen(false);
            }}
            cancel={() => {
              setIsDialogOpen(false);
            }}
            readMode={false}
          />
        )}
      </Panel>
      {showComparePanel && (
        <ComparePanel data={assessment} onClose={() => setShowComparePanel(false)} />
      )}
    </div>
  );
}
